import { useChatThemeStore } from '../../store/chatThemeStore'
import { SwatchIcon, XMarkIcon } from '@heroicons/react/24/outline'

const ChatThemePicker = () => {
  const { 
    currentTheme, 
    availableThemes, 
    isThemePickerOpen, 
    setTheme, 
    toggleThemePicker, 
    closeThemePicker 
  } = useChatThemeStore()

  if (!isThemePickerOpen) return null

  const handleThemeSelect = (themeId) => {
    setTheme(themeId)
    closeThemePicker()
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={closeThemePicker}
      />
      
      {/* Theme Picker Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Choose Chat Theme
            </h3>
            <button
              onClick={closeThemePicker}
              className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Theme Grid */}
          <div className="p-4 space-y-3">
            {Object.values(availableThemes).map((theme) => (
              <div
                key={theme.id}
                onClick={() => handleThemeSelect(theme.id)}
                className={`
                  relative cursor-pointer rounded-lg border-2 transition-all duration-200 overflow-hidden
                  ${currentTheme === theme.id 
                    ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800' 
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }
                `}
              >
                {/* Theme Preview */}
                <div className={`h-20 ${theme.background} relative`}>
                  {theme.pattern && (
                    <div 
                      className="absolute inset-0"
                      style={{ backgroundImage: `url('${theme.pattern}')` }}
                    />
                  )}
                  
                  {/* Sample Message Bubbles */}
                  <div className="absolute inset-0 p-3 flex flex-col justify-center space-y-1">
                    <div className="bg-white bg-opacity-90 rounded-lg px-2 py-1 text-xs max-w-[60%] ml-auto">
                      Your message
                    </div>
                    <div className="bg-gray-300 bg-opacity-90 rounded-lg px-2 py-1 text-xs max-w-[60%]">
                      Friend's reply
                    </div>
                  </div>

                  {/* Selected Indicator */}
                  {currentTheme === theme.id && (
                    <div className="absolute top-2 right-2">
                      <div className="bg-primary-500 text-white rounded-full p-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

                {/* Theme Name */}
                <div className="p-3">
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {theme.name}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Themes are saved automatically and apply to all chats
            </p>
          </div>
        </div>
      </div>
    </>
  )
}

export default ChatThemePicker